// Phase 85.A.72 — Modal carousel hướng dẫn khi avatar bị reject vì không
// nhận diện được khuôn mặt người (Gemini 2.5 Flash vision). Mở tự động khi
// user fail ≥ 2 lần trong session (sessionStorage flag).
//
// User choice 1d — banner đỏ inline + popup hướng dẫn nếu fail nhiều lần.
import { useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  X, CaretLeft, CaretRight, User, Camera, IdentificationCard, ArrowRight, Warning,
} from "@phosphor-icons/react";

const STEPS = [
  {
    id: "frontal",
    icon: User,
    color: "#34D8FF",
    title: "1. Chụp chính diện gương mặt",
    desc: "Quay điện thoại về phía bạn, để gương mặt CHIẾM khoảng 60-70% khung hình. Không che mặt bằng tóc, kính râm tối, mũ rộng vành.",
  },
  {
    id: "lighting",
    icon: Camera,
    color: "#A8FF00",
    title: "2. Đủ ánh sáng + nét",
    desc: "Tránh chụp ngược sáng (cửa sổ phía sau) hoặc trong phòng tối. Ảnh phải rõ nét, KHÔNG bị mờ rung, KHÔNG quá nhiều grain/noise.",
  },
  {
    id: "must-be-you",
    icon: IdentificationCard,
    color: "#FF8A00",
    title: "3. Phải là ảnh BẠN",
    desc: "Không dùng logo công ty, ảnh động vật, ảnh nhóm nhiều người, biểu tượng hay nhân vật anime. AI sẽ tự động từ chối các loại ảnh này.",
  },
];

export default function AvatarFailureGuideModal({ open, onClose, onRetry, errorDetail }) {
  const [stepIdx, setStepIdx] = useState(0);
  const step = STEPS[stepIdx];
  const StepIcon = step.icon;
  const isLast = stepIdx === STEPS.length - 1;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pl-fixed-fullscreen z-[400] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-md px-3 pb-4 sm:pb-0"
          onClick={onClose}
          data-testid="avatar-failure-guide-modal"
        >
          <motion.div
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md rounded-3xl border border-amber-400/35 bg-gradient-to-br from-[#1A1208] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(255,138,0,0.4)] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3.5 flex items-start gap-3 border-b border-white/8 bg-gradient-to-r from-amber-500/12 to-transparent">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500/35 to-orange-500/20 border border-amber-400/45 flex items-center justify-center shrink-0">
                <Warning size={20} weight="fill" className="text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300/85">
                  Ảnh chưa hợp lệ
                </div>
                <div className="text-[15px] font-black text-white leading-tight mt-0.5">
                  Hướng dẫn chụp avatar chuẩn
                </div>
                {errorDetail ? (
                  <div className="text-[11.5px] text-amber-100/75 mt-1 italic">
                    {errorDetail}
                  </div>
                ) : null}
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-xl hover:bg-white/8 flex items-center justify-center text-white/55 hover:text-white transition"
                data-testid="avatar-failure-guide-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <div className="px-5 py-5">
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0"
                  style={{
                    background: `${step.color}24`,
                    borderColor: `${step.color}55`,
                  }}
                >
                  <StepIcon size={24} weight="duotone" style={{ color: step.color }} />
                </div>
                <div className="flex-1">
                  <div className="text-[15px] font-black text-white leading-tight">
                    {step.title}
                  </div>
                </div>
              </div>
              <p className="text-[12.5px] text-white/75 leading-relaxed">
                {step.desc}
              </p>

              <div className="flex items-center justify-center gap-1.5 mt-5">
                {STEPS.map((s, i) => (
                  <button
                    key={s.id}
                    onClick={() => setStepIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === stepIdx
                        ? "w-8 bg-amber-400"
                        : "w-1.5 bg-white/20 hover:bg-white/40"
                    }`}
                    aria-label={`Bước ${i + 1}`}
                  />
                ))}
              </div>
            </div>

            <div className="px-5 py-3.5 flex items-center gap-2 border-t border-white/8 bg-black/30">
              <button
                type="button"
                onClick={() => setStepIdx((p) => Math.max(0, p - 1))}
                disabled={stepIdx === 0}
                className="h-10 px-3 rounded-xl border border-white/10 hover:bg-white/5 text-white/65 text-[12px] font-bold transition disabled:opacity-30 flex items-center gap-1.5"
                data-testid="avatar-failure-guide-prev"
              >
                <CaretLeft size={14} weight="bold" />
                Quay lại
              </button>
              <div className="flex-1" />
              {!isLast ? (
                <button
                  type="button"
                  onClick={() => setStepIdx((p) => Math.min(STEPS.length - 1, p + 1))}
                  className="h-10 px-3.5 rounded-xl bg-white/8 hover:bg-white/15 text-white text-[12px] font-black transition flex items-center gap-1.5"
                  data-testid="avatar-failure-guide-next"
                >
                  Tiếp
                  <CaretRight size={14} weight="bold" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { onClose?.(); onRetry?.(); }}
                  className="h-10 px-4 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:shadow-[0_8px_20px_rgba(255,138,0,0.4)] text-white text-[12px] font-black transition flex items-center gap-1.5 active:scale-[0.98]"
                  data-testid="avatar-failure-guide-retry"
                >
                  <Camera size={14} weight="fill" />
                  Chụp lại
                  <ArrowRight size={13} weight="bold" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
